import { NgClass } from "@angular/common";
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CollectedItemDetails, CollectedPreviewItem, DataResponse } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { itemTypeToId } from "@ponder-shared/utils/enumConverter";
import { gameTypeToId } from "@ponder-shared/utils/gameInfo";
import { canFlip } from "@ponder-shared/utils/general";
import { Subscription, take } from "rxjs";
import { environment } from "../../../environments/environment";
import { ItemSizeService } from "./item-size.service";
import { AuthService } from "@ponder-core/auth/auth.service";
import { getFromLocalStorage, LocalStorageKey } from "@ponder-shared/utils/localStorage";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { FormatCurrencyPipe } from "@ponder-shared/pipes/format-currency.pipe";
import { FormatNumberPipe } from "@ponder-shared/pipes/format-number.pipe";

@Component({
    selector: "preview-item",
    imports: [NgClass, FormatCurrencyPipe, FormatNumberPipe],
    templateUrl: "./preview-item.component.html"
})
export class PreviewItemComponent implements AfterViewInit {
    @ViewChild("previewItemContainer") previewItemContainerRef!: ElementRef<HTMLElement>;
    @ViewChild("previewImage") previewImageRef!: ElementRef<HTMLImageElement>;

    @Input({ required: true }) public previewItem!: CollectedPreviewItem;
    @Input({ required: true }) public loadingStrategy!: String;
    @Input({ required: false }) public sizeModifier: number = 1;
    @Input({ required: false }) public hideHighlight: boolean = false;
    @Input({ required: false }) public showOnlyCollectedValue: boolean = false;
    @Input({ required: false }) public defaultControlVisibility?: boolean;

    @Input() public isCollection: boolean = false;

    public readonly flipSvgIcon = SvgIconMap.get(SvgIcon.Flip)!;

    public processing: boolean = false;
    public isAuthed: boolean = false;

    public controlsVisible: boolean = false;
    private toggleOnControls: boolean = false;
    private forceControlsOn: boolean = false;

    private gameId!: number;
    private itemTypeId!: number;
    private isFlipped: boolean = false;

    private resizeEventSub!: Subscription;
    private controlsToggledSub!: Subscription;

    public constructor(private auth: AuthService, private ponderDataClient: PonderDataClient, private events: EventBroker, private itemSize: ItemSizeService) { 
            this.resizeEventSub = this.events.ui.windowResizeEvent.subscribe({
                next: () => {
                    this.updateControlVisibility();
                    this.resizeItem();
                }
            });

            this.controlsToggledSub = this.events.ui.previewItemControlsToggledEvent.subscribe({
                next: () => {
                    this.updateControlVisibility();
                }
            });
    }

    public ngOnDestroy(): void {
        this.resizeEventSub.unsubscribe();
        this.controlsToggledSub.unsubscribe();
    }

    public ngOnInit() {
        this.isAuthed = this.auth.isAuthed();
        this.gameId = gameTypeToId(this.previewItem.item.game!);
        this.itemTypeId = itemTypeToId(this.previewItem.item.itemType!);
        this.updateControlVisibility();
    }

    public ngAfterViewInit() {
        this.resizeItem();
        this.handleFade();
    }

    private resizeItem(): void {
        if (this.previewItemContainerRef) {
            this.previewItemContainerRef.nativeElement.style.setProperty("width", `${this.itemSize.width * this.sizeModifier}px`);
            this.previewItemContainerRef.nativeElement.style.setProperty("height", `${this.itemSize.height * this.sizeModifier}px`);
        }
    }

    public onHoverEnter(): void {
        this.toggleOnControls = true;
        this.updateControlVisibility();
    }

    public onHoverExit(): void {
        this.toggleOnControls = false;
        this.updateControlVisibility();
    }

    private updateControlVisibility(): void {
        var controlsToggledOn = getFromLocalStorage(LocalStorageKey.PreviewItemControlsVisibility, "visible") == "visible";
        this.forceControlsOn = (this.defaultControlVisibility == undefined) ? controlsToggledOn : this.defaultControlVisibility;
        this.controlsVisible = this.toggleOnControls || this.forceControlsOn;
    }

    public setPreviewItem(item: CollectedPreviewItem): void {
        this.previewItem = item;
        this.handleFade();
    }

    public getPreviewImageUrl(): string {
        if (!this.isFlipped) {
            return `${environment.ponderImageBaseUrl}/card/medium/front/${this.previewItem.item.id}.jpg`;
        } else {
            return `${environment.ponderImageBaseUrl}/card/medium/back/${this.previewItem.item.id}.jpg`;
        }
    }

    public getPreviewUrl(): string {
        if (this.previewItem.item.url) {
            // If the server sent back a URL, use it.
            return `${this.previewItem.item.url!}`;
        } else {
            // Otherwise route to the generic item url and let it figure it out
            return `/item/${this.previewItem.item.id}`;
        }
    }

    public hasAnyPrice(): boolean {
        return this.hasPrice() || this.hasSpecialPrice();
    }

    public hasPrice(): boolean {
        if (this.previewItem.price.normalUSD > 0) {
            if (this.showOnlyCollectedValue && !this.previewItem.collected?.count) {
                return false;
            } else {
                return true;
            }
        }

        return false;
    }

    public hasSpecialPrice(): boolean {
        if (this.previewItem.price.specialUSD > 0) {
            if (this.showOnlyCollectedValue && !this.previewItem.collected?.specialCount) {
                return false;
            } else {
                return true;
            }
        }

        return false;
    }

    public shouldHighlight(): boolean {
        if (this.isCollection) {
            return false;
        } else if (this.previewItem.collected?.count || this.previewItem.collected?.specialCount) {
            return true && !this.hideHighlight;
        } else {
            return false;
        }
    }

    public canFlip(): boolean {
        if (this.previewItem.item.data?.magic?.layout) {
            return canFlip(this.previewItem.item.data.magic.layout);
        } else {
            return false;
        }
    }

    // -------------------------------------------------------------------------------------
    // Add / Remove from Collection
    // -------------------------------------------------------------------------------------

    public canCollectNormal(): boolean {
        if (this.isAuthed) {
            // todo support other games and items
            if (this.previewItem.item.data?.magic) {
                return this.previewItem.item.data!.magic!.treatment.normal;
            }
        }

        return false;
    }

    public canCollectSpecial(): boolean {
        if (this.isAuthed) {
            // todo support other games and items
            if (this.previewItem.item.data?.magic) {
                return this.previewItem.item.data!.magic!.treatment.special;
            }
        }

        return false;
    }

    public setNormalCount(count: number): void {
        if (Number.isNaN(count) || (count < 0)) {
            return;
        }
        
        count = Math.min(count, 1024);
        var current = this.previewItem.collected?.count ?? 0;

        if (count == current) {
            return;
        }

        this.adjustCount(count - current, 0, 0);
    }

    public setSpecialCount(count: number): void {
        if (Number.isNaN(count) || (count < 0)) {
            return;
        }
        
        count = Math.min(count, 1024);
        var current = this.previewItem.collected?.specialCount ?? 0;

        if (count == current) {
            return;
        }

        this.adjustCount(0, count - current, 0);
    }

    public setProxyCount(count: number): void {
        if (Number.isNaN(count) || (count < 0)) {
            return;
        }
        
        count = Math.min(count, 1024);
        var current = this.previewItem.collected?.proxyCount ?? 0;

        if (count == current) {
            return;
        }

        this.adjustCount(0, 0, count - current);
    }

    public adjustCount(normal: number, special: number, proxy: number): void {
        // Note this method is essentially copy-paste of item-collection-count.component.ts:adjustCount
        // Maybe combine these into something ...
        if (this.processing) {
            return;
        }

        this.processing = true;

        var collectedInfo = this.previewItem.collected ?? new CollectedItemDetails({ count: 0, specialCount: 0, proxyCount: 0 });

        var newCount = (collectedInfo.count ? collectedInfo.count! + normal : normal);
        var newSpecialCount = (collectedInfo.specialCount ? collectedInfo.specialCount! + special : special);
        var newProxyCount = (collectedInfo.proxyCount ? collectedInfo.proxyCount! + proxy : proxy);
        
        var wasInCollection = (collectedInfo.count || collectedInfo.specialCount || collectedInfo.proxyCount) ? true : false;
        var newToCollection = (!wasInCollection && (newCount || newSpecialCount || newProxyCount)) ? true : false;
        var removedFromCollection = (wasInCollection && (!newCount && !newSpecialCount && !newProxyCount)) ? true : false;

        if ((newCount < 0) || (newCount > 1024) || (newSpecialCount < 0) || (newSpecialCount > 1024) || (newProxyCount < 0) || (newProxyCount > 1024)) {
            // Can't have negative collected, and don't exceed our arbitrary limit of 1024. tbd if thats needed/wanted
            this.processing = false;
            return;
        }

        // Immediately update the rendered count for UI responsiveness (this may be rolled back if API returns error)
        collectedInfo.count = newCount;
        collectedInfo.specialCount = newSpecialCount;
        collectedInfo.proxyCount = newProxyCount;

        this.previewItem.collected = collectedInfo;
        
        // Update now for immediate feedback on the client
        this.handleFade();

        this.ponderDataClient
            .collectionItem(this.previewItem.item.id, normal, special, proxy, this.gameId, this.itemTypeId)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (response.success) {
                        this.events.collection.collectionModifiedEvent.emit({
                            itemId: this.previewItem.item.id!,
                            newToCollection: newToCollection,
                            removedFromCollection: removedFromCollection,
                            countAdjustment: normal,
                            valueAdjustment: normal * this.previewItem.price.normalUSD,
                            specialCountAdjustment: special,
                            specialValueAdjustment: special * this.previewItem.price.specialUSD,
                            proxyCountAdjustment: proxy
                        });
                    // Rollback the previous UI update if error
                    } else {
                        this.previewItem.collected!.count! -= normal;
                        this.previewItem.collected!.specialCount! -= special;
                        this.previewItem.collected!.proxyCount! -= proxy;
                    }
                    
                    if (response.error) {
                        this.events.ui.displayErrorEvent.emit({
                            message: response.error
                        });
                    }
                    
                    this.processing = false;
                },
                error: (_: any) => {
                    this.previewItem.collected!.count! -= normal;
                    this.previewItem.collected!.specialCount! -= special;
                    this.previewItem.collected!.proxyCount! -= proxy;
                    this.processing = false;
                }
            })
            .add(() => {
                // Update again once we have the real counts from the server
                this.handleFade();
            });
    }

    private handleFade(): void {
        if (!this.previewImageRef) {
            return;
        }

        var opacity: string = "1.0";

        var hasNormal: boolean = (this.previewItem.collected?.count ? true : false);
        var hasSpecial: boolean = (this.previewItem.collected?.specialCount ? true : false);
        var hasProxy: boolean = (this.previewItem.collected?.proxyCount ? true : false);

        if (this.isCollection && !hasNormal && !hasSpecial && !hasProxy) {
            // Removed from collection
            opacity = "0.2";
        }

        if (!hasNormal && !hasSpecial && hasProxy) {
            // Proxy only
            opacity = "0.7";
        }

        this.previewImageRef.nativeElement.style.setProperty("opacity", opacity);
    }
    
    // -------------------------------------------------------------------------------------
    // Other Actions
    // -------------------------------------------------------------------------------------

    public onFlipClicked(): void {
        if (!this.canFlip()) {
            return;
        }

        this.isFlipped = !this.isFlipped;
    }

    public onContextMenu(event: MouseEvent): void {
        if (this.isAuthed) {
            event.preventDefault();
            
            this.events.ui.togglePreviewItemContextMenuEvent.emit({ 
                itemComponent: this,
                clickX: event.x,
                clickY: event.y 
            });
        }
    }
}